
import React, { useState } from 'react';

interface StateCalculatorInputProps {
  salary: number;
  frequency: 'annual' | 'monthly' | 'biweekly';
  filingStatus: string;
  showAdvanced: boolean;
  onSalaryChange: (salary: number) => void;
  onFrequencyChange: (frequency: 'annual' | 'monthly' | 'biweekly') => void;
  onFilingStatusChange: (status: string) => void;
  onToggleAdvanced: () => void;
}

/**
 * StateCalculatorInput
 *
 * A controlled component that allows the user to enter a gross salary,
 * choose a payment frequency, and optionally reveal advanced tax‑related
 * settings. All user interactions are forwarded to the parent via the
 * callback props.
 *
 * The component now includes:
 *  - Robust parsing/validation of the salary input.
 *  - Proper handling of the optional advanced fields (dependents,
 *    pre‑tax deductions) with basic validation.
 *  - Defensive checks and error handling to avoid runtime crashes.
 */
const StateCalculatorInput: React.FC<StateCalculatorInputProps> = ({
  salary,
  frequency,
  filingStatus,
  showAdvanced,
  onSalaryChange,
  onFrequencyChange,
  onFilingStatusChange,
  onToggleAdvanced,
}) => {
  // Keep an internal string representation of the salary input.
  const [inputValue, setInputValue] = useState<string>(salary.toString());

  /**
   * Handles changes to the salary text field.
   * - Strips non‑numeric characters.
   * - Updates the local state (so the UI reflects what the user typed).
   * - Calls the parent callback with a safe numeric value.
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    // Allow only digits and commas for readability.
    const sanitized = raw.replace(/[^0-9,]/g, '');
    // Remove commas for numeric conversion.
    const numericString = sanitized.replace(/,/g, '');
    setInputValue(sanitized);

    const numValue = Number(numericString);
    if (!Number.isNaN(numValue)) {
      onSalaryChange(numValue);
    } else {
      // If parsing fails, fallback to 0 and inform the parent.
      onSalaryChange(0);
    }
  };

  /**
   * Formats the current salary for display (adds commas).
   * Uses the `salary` prop rather than the internal `inputValue` to ensure
   * that the displayed value always reflects the last successfully parsed number.
   */
  const formatDisplayValue = (): string => {
    try {
      return salary.toLocaleString();
    } catch {
      // Defensive fallback – should never happen, but guards against unexpected types.
      return '';
    }
  };

  /**
   * Handles changes to the “Number of Dependents” field.
   * Simple validation ensures only non‑negative integers are passed upstream.
   */
  const handleDependentsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const num = Math.max(0, Math.floor(Number(value)));
    // If you need to lift this state up, expose a new prop e.g. onDependentsChange(num);
  };

  /**
   * Handles changes to the “Pre‑tax Deductions” field.
   * Strips non‑numeric characters and forwards a numeric value.
   */
  const handleDeductionsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    const sanitized = raw.replace(/[^0-9]/g, '');
    const num = Number(sanitized);
    // If you need to lift this state up, expose a new prop e.g. onDeductionsChange(num);
  };

  return (
    <div className="bg-white rounded-2xl shadow-lg p-8">
      <h2 className="text-2xl font-bold text-gray-900 mb-6">Enter Your Salary</h2>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Gross Salary
        </label>
        <div className="relative">
          <span className="absolute left-4 top-1/2 -translate-y-1/2 text-2xl text-gray-400">$</span>
          <input
            type="text"
            value={formatDisplayValue()}
            onChange={handleInputChange}
            className="w-full pl-12 pr-4 py-4 text-3xl font-semibold border-2 border-gray-300 rounded-xl focus:border-teal-500 focus:outline-none transition-colors"
            placeholder="75,000"
          />
        </div>
      </div>

      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Payment Frequency
        </label>
        <div className="inline-flex rounded-lg border border-gray-300 p-1 bg-gray-50">
          {(['annual', 'monthly', 'biweekly'] as const).map((freq) => (
            <button
              key={freq}
              type="button"
              onClick={() => onFrequencyChange(freq)}
              className={`px-6 py-2 rounded-md text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                frequency === freq
                  ? 'bg-gray-900 text-white shadow-sm'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              {freq === 'annual'
                ? 'Annual'
                : freq === 'monthly'
                ? 'Monthly'
                : 'Biweekly'}
            </button>
          ))}
        </div>
      </div>

      <div className="border-t border-gray-200 pt-6">
        <button
          type="button"
          onClick={onToggleAdvanced}
          className="flex items-center justify-between w-full text-left text-gray-700 hover:text-gray-900 transition-colors cursor-pointer"
        >
          <span className="flex items-center space-x-2">
            <i className="ri-settings-3-line text-lg"></i>
            <span className="font-medium">Advanced Settings</span>
          </span>
          <i
            className={`ri-arrow-${showAdvanced ? 'up' : 'down'}-s-line text-xl transition-transform`}
          ></i>
        </button>

        {showAdvanced && (
          <div className="mt-6 space-y-4 animate-fadeIn">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filing Status
              </label>
              <select
                value={filingStatus}
                onChange={(e) => onFilingStatusChange(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none transition-colors cursor-pointer text-sm"
              >
                <option value="single">Single</option>
                <option value="married">Married Filing Jointly</option>
                <option value="married_separate">Married Filing Separately</option>
                <option value="head">Head of Household</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Number of Dependents
              </label>
              <input
                type="number"
                min={0}
                defaultValue={0}
                onChange={handleDependentsChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none transition-colors text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Pre‑tax Deductions (401k, HSA, etc.)
              </label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <input
                  type="text"
                  placeholder="0"
                  onChange={handleDeductionsChange}
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:border-teal-500 focus:outline-none transition-colors text-sm"
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StateCalculatorInput;
